import { auth,db } from 'firebase/firebase';
import { collection, deleteDoc, doc, getDoc, getDocs, query,setDoc, Timestamp, where } from 'firebase/firestore';

import { createOperation } from '@src/util/firebase-operations/utils/createOperation';
import safeExecute from '@src/util/safeExecute';

import elasticQueryClient from './ElasticQueryClient';

import { FSCollections } from '../constants/database';


class ShiftRepository {
  constructor() {
    this.shifts = [];
    this.shiftsCollection = FSCollections.WORK_SHIFTS;
    this.organizationCollection = FSCollections.ORGANIZATIONS;
    this.workJourneyConfigsCollection = FSCollections.WORK_JOURNEY_CONFIGS;
  }

  getShiftsPath(orgId) {
    return `${this.organizationCollection}/${orgId}/${this.shiftsCollection}`;
  }

  getWorkJourneyConfigsPath(orgId) {
    return `${this.organizationCollection}/${orgId}/${this.workJourneyConfigsCollection}`;
  }

  getPoliciesPath(orgId, shiftId) {
    return `${this.getShiftsPath(orgId)}/${shiftId}/policies`;
  }

  // Create
  async create(shiftData, organizationId) {
    return safeExecute(async () => {
      const res = await createOperation(
        this.getShiftsPath(organizationId),
        null,
        shiftData,
        'create',
      );
      return res;
    });
  }

  // Update
  async update(shiftData, organizationId) {
    return safeExecute(async () => {
      const { id, ...body } = shiftData;
      const res = await createOperation(
        this.getShiftsPath(organizationId),
        shiftData.id,
        body,
        'update',
      );
      return res;
    });
  }

  // Delete
  async delete(shiftId, organizationId) {
    return safeExecute(async () => {
      // Delete policies first
      await this.deleteResolutions(shiftId, organizationId);
      
      // Then delete the shift
      await createOperation(
        this.getShiftsPath(organizationId), 
        shiftId,
        null,
        'delete',
      );
    });
  }

  // Get
  async get(shiftId, organizationId) {
    return safeExecute(async () => {
      const shiftRef = doc(db, this.getShiftsPath(organizationId), shiftId);
      const res = await getDoc(shiftRef);
      if (!res.exists()) return null;
      
      const shiftData = res.data();
      
      // Get policies from subcollection
      const policiesRef = collection(db, this.getPoliciesPath(organizationId, shiftId));
      const policiesSnapshot = await getDocs(policiesRef);
      const policies = policiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Add resolution data for backward compatibility
      if (policies.length > 0) {
        const latestPolicy = policies[policies.length - 1];
        shiftData.resolution = {
          id: latestPolicy.id,
          name: latestPolicy.name,
          date: latestPolicy.date
        };
      }
      
      return shiftData;
    });
  }

    // Create resolution in policies subcollection
  async createResolution(shiftId, organizationId, resolutionData) {
    return safeExecute(async () => {
      if (!resolutionData?.name && !resolutionData?.date) {
        return null;
      }

      const policiesRef = collection(db, this.getPoliciesPath(organizationId, shiftId));
      const newDocRef = doc(policiesRef);
      
      const resolution = {
        id: newDocRef.id,
        name: resolutionData.name || '',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        uid: auth.currentUser.uid,
        ...(resolutionData.date ? { date: resolutionData.date } : {}),
      };
      
      await setDoc(newDocRef, resolution);
      return resolution;
    });
  }

  // Get all resolutions for a shift
  async getResolutions(shiftId, organizationId) {
    return safeExecute(async () => {
      const policiesRef = collection(db, this.getPoliciesPath(organizationId, shiftId));
      const snapshot = await getDocs(policiesRef);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    });
  }

  // Delete all resolutions for a shift
  async deleteResolutions(shiftId, organizationId) {
    return safeExecute(async () => {
      const policiesRef = collection(db, this.getPoliciesPath(organizationId, shiftId));
      const snapshot = await getDocs(policiesRef);
      
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
    });
  }

  // Get all work journeys by shift id
  async getWorkJourneysByShiftId(shiftId, organizationId) {
    return safeExecute(async () => {
      const url = this.getWorkJourneyConfigsPath(organizationId);
      const workJourneysRef = collection(db, url);
      const qry = query(workJourneysRef, where('workShift.id', '==', shiftId))
      const snapshot = await getDocs(qry);
      const res = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return res;
   
    });
  }

  queryElastic(params) {
    return elasticQueryClient.query(params);
  }
}

export default ShiftRepository;
